import {FormEvent, useState} from 'react';

import styled from 'styled-components';

import {attachModal} from '~/shared/components/ReduxModal';
import {media} from '~/shared/theme/media';
import {MoneycardOtlRuleManagement} from '~/shared/store/models/MoneycardOtlRuleManagement';
import {getOtlRuleCardId} from '~/shared/components/Otl/utils';
import {PieCloseModalButton} from '~/shared/components/Modals/styled';
import {flipOnLTR} from '~/shared/theme/utils';
import {upsertOtlRule} from '~/shared/services/bffService/api';
import {MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {FlexColumn} from '~/shared/theme/FlexLayout';
import {trackEvent} from '~/shared/services/analytics';
import {navigateToDefaultStartPage} from '~/shared/services/navigation';
import {getNonExpiredModifiedCreditCards} from '~/shared/utils/payments/payments';

import LinkMoneycardForm from './LinkMoneycardForm';
import SuccessView from './SuccessView';

const Root = styled(FlexColumn)`
  position: relative;
  width: 100%;
  height: 100%;
  ${media.minLargeMobile`
    height: auto;
    max-width: 500px;
  `}

  && {
    background-color: ${({theme}) => theme.colors.surface};
  }
`;

const StyledPieCloseModalButton = styled(PieCloseModalButton)`
  padding: 6px;
  top: 16px;
  ${flipOnLTR`
    left: 16px;
  `}
`;

type LinkMoneycardModalProps = {
  args: {
    currentMoneycard: MoneyCard;
    otlData: MoneycardOtlRuleManagement;
    addCreditCardAction: () => void;
    hasSuccessfullyAddedCreditCard?: boolean;
    onLinkFailed?: () => void;
    openOtlAction?: () => void;
    onSuccess?: () => void;
  };
  closeModal: () => void;
};

const LinkMoneycardModal = ({args, closeModal}: LinkMoneycardModalProps) => {
  const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] = useState(false);
  const {
    otlData,
    addCreditCardAction,
    hasSuccessfullyAddedCreditCard,
    currentMoneycard,
    onLinkFailed,
    openOtlAction,
    onSuccess,
  } = args;
  const {userCreditcards: rawCards} = otlData;
  const [currentCardId, setCurrentCardId] = useState<number | null>(getOtlRuleCardId(otlData));
  const [isDropdownTouched, setIsDropdownTouched] = useState(false);
  const [isTermsOfUseConfirmed, setIsTermsOfUseConfirmed] = useState(false);

  const toggleCheckbox = () => setIsTermsOfUseConfirmed(s => !s);

  const userCreditcards = getNonExpiredModifiedCreditCards(rawCards);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    trackEvent('linkCC2MC_submit');
    e.preventDefault();
    if (currentCardId) {
      try {
        await upsertOtlRule({
          encryptedMoneycardId: currentMoneycard.id,
          creditcardId: currentCardId,
          ruleActive: true,
        });
        trackEvent('linkCC2MC_success');
        if (onSuccess) {
          onSuccess();
        }
        setHasSubmittedSuccessfully(true);
      } catch (error) {
        if (is401Error(error)) {
          handleRefreshToken(error, onSubmit, e);
          return;
        }
        trackEvent('linkCC2MC_failed', {errorMessage: error instanceof Error ? error.message : 'Something went wrong'});
        if (onLinkFailed) {
          onLinkFailed();
        }
      }
    }
  };

  const onSuccessCTAClick = () => {
    closeModal();
    navigateToDefaultStartPage();
  };

  return (
    <Root>
      <StyledPieCloseModalButton onClick={closeModal} />
      {!hasSubmittedSuccessfully && (
        <LinkMoneycardForm
          {...{
            onSubmit,
            currentCardId,
            setCurrentCardId,
            userCreditcards,
            addCreditCardAction,
            hasSuccessfullyAddedCreditCard,
            isDropdownTouched,
            setIsDropdownTouched,
            isTermsOfUseConfirmed,
            toggleCheckbox,
          }}
        />
      )}
      {hasSubmittedSuccessfully && (
        <SuccessView onCTAClick={onSuccessCTAClick} onLinkClick={openOtlAction} />
      )}
    </Root>
  );
};

LinkMoneycardModal.hideCloseButton = true;
LinkMoneycardModal.modalType = 'autoWidth';
attachModal('linkMoneycardModal', LinkMoneycardModal);

export default LinkMoneycardModal;
